// @ts-check

import { createClient } from 'graphqurl';
import { extractAgencyFromUrl } from './utils/url';
import { getVariableOrDefault } from './utils/env';

const defaultAgencyProvider = () => {
  return extractAgencyFromUrl(window.location.hostname) || 'default';
};

const defaultThemeServiceUrl = getVariableOrDefault(
  'VUE_APP_THEME_SERVICE_URL',
  'https://c360-be.stg.adportal.io/graphql',
);

const currentThemeQuery = `
  query ($agency: String!) {
    theme(agency: $agency) {
      name
      portalName
      css
      copyright
      footerLogo
      colors {
        primary
        gradient
        background
      }
      logo
      portalUrls {
        staging
        production
        development
      }
      favicon
      enabled
    }
  }
`;

const defaultThemeLoader = async (agency, fromUrl = defaultThemeServiceUrl) => {
  const gqlClient = createClient({
    endpoint: fromUrl,
    headers: {
      'content-type': 'application/json',
    },
  });

  const { data } = await gqlClient.query({
    query: currentThemeQuery,
    variables: { agency },
  });

  return data.theme;
};

const DEFAULT_PARAMS = {
  agencyProvider: defaultAgencyProvider,
  themeLoader: defaultThemeLoader,
};

/**
 * Get theme for the current agency from the API.
 *
 * @example
 * ```js
 * getTheme().then((theme) => {
 *   const product = 'admin hierarchy';
 *   Vue.use(C360, { product, theme });
 *   new Vue({});
 * });
 * ```
 *
 * @example If you need to provide custom agency mechanism:
 * ```js
 * const agencyProvider = () => localStorage.get('agency');
 *
 * getTheme({ agencyProvider }).then((theme) => {
 *   Vue.use(C360, { theme });
 * });
 * ```
 *
 * @param {{
 *  agencyProvider?: () => string,
 *  themeLoader?: (agency: string) => Promise<import('./themes').C360Theme | null>;
 * }} params
 *
 * @returns {Promise<import('./themes').C360Theme | null>}
 */
export const getTheme = async ({
  agencyProvider = defaultAgencyProvider,
  themeLoader = defaultThemeLoader,
} = DEFAULT_PARAMS) => {
  try {
    // get current agency
    const agency = agencyProvider();

    // get theme for agency
    const theme = await themeLoader(agency);

    // null means theme doesn't exists
    // so, returning generic theme
    if (theme === null || !theme.enabled) {
      const defaultTheme = await themeLoader('agency');
      return defaultTheme;
    }

    return theme;
  } catch (error) {
    // if any error - return generic theme
    const defaultTheme = await themeLoader('agency');
    return defaultTheme ;
  }
};
